import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, Printer, ShieldCheck, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAudit, useAuditItems, useAuditSummary, useCompleteAudit, useSignoffAudit } from "@/features/audits/api";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { canSignoffAudit } from "@/lib/roles";
import { fmtDateTime } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/audits/$auditId/signoff")({
  component: AuditSignoff,
});

function AuditSignoff() {
  const { auditId } = Route.useParams();
  const { data: audit } = useAudit(auditId);
  const { data: me } = useCurrentUser();
  const { data: summary } = useAuditSummary(auditId);
  const { data: items = [] } = useAuditItems(auditId);
  const complete = useCompleteAudit();
  const signoff = useSignoffAudit();

  if (!audit) return <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  const canSign = canSignoffAudit(me?.roles ?? []);
  const openDiscrepancies = (summary?.discrepancies ?? 0) - (summary?.reconciled ?? 0);

  const onComplete = async () => {
    try { await complete.mutateAsync(auditId); toast.success("Audit marked Completed"); }
    catch (e) { toast.error((e as Error).message); }
  };

  const [confirmOpen, setConfirmOpen] = useState(false);

  const onSignoff = async () => {
    try { 
      await signoff.mutateAsync(auditId); 
      toast.success("Audit signed off"); 
      setConfirmOpen(false);
    } catch (e) { 
      toast.error((e as Error).message); 
    }
  };

  const printReport = () => {
    const w = window.open("", "_blank", "width=1000,height=800");
    if (!w) return;
    const rows = items.map((it) => `
      <tr>
        <td>${it.is_untagged_stub ? "(untagged)" : it.asset?.asset_code ?? ""}</td>
        <td>${it.is_untagged_stub ? it.untagged_description ?? "" : it.asset?.name ?? ""}</td>
        <td>${it.expected_location?.name ?? "—"}</td>
        <td>${it.found_location?.name ?? "—"}</td>
        <td>${it.condition ?? "—"}</td>
        <td>${it.discrepancy_type}</td>
        <td>${it.reconciliation_action ?? ""}</td>
      </tr>`).join("");
    w.document.write(`<!doctype html><html><head><title>Audit Report — ${audit.audit_name}</title>
      <style>body{font-family:system-ui;padding:20px;font-size:12px}h1{font-size:18px}h2{font-size:14px;margin-top:18px}table{width:100%;border-collapse:collapse;margin-top:8px}th,td{border:1px solid #ccc;padding:4px;text-align:left}th{background:#f3f4f6}</style>
      </head><body>
      <h1>Audit Report</h1>
      <div><b>${audit.audit_name}</b> · ${audit.audit_type} · ${audit.status}</div>
      <div>Period: ${audit.start_date ?? "—"} → ${audit.end_date ?? "—"}</div>
      ${audit.signed_off_at ? `<div>Signed off: ${fmtDateTime(audit.signed_off_at)}</div>` : ""}
      <h2>Summary</h2>
      <table><tr><th>Total</th><th>Verified</th><th>Pending</th><th>Not Found</th><th>Wrong Loc</th><th>Damaged</th><th>Untagged</th><th>Reconciled</th></tr>
      <tr><td>${summary?.total ?? 0}</td><td>${summary?.verified ?? 0}</td><td>${summary?.pending ?? 0}</td><td>${summary?.not_found ?? 0}</td><td>${summary?.wrong_location ?? 0}</td><td>${summary?.damaged ?? 0}</td><td>${summary?.untagged ?? 0}</td><td>${summary?.reconciled ?? 0}</td></tr></table>
      <h2>Items</h2>
      <table><thead><tr><th>Code</th><th>Name</th><th>Expected</th><th>Found</th><th>Condition</th><th>Discrepancy</th><th>Reconciliation</th></tr></thead><tbody>${rows}</tbody></table>
      <script>window.onload=()=>window.print();</script>
      </body></html>`);
    w.document.close();
  };

  return (
    <div className="space-y-4">
      <Card><CardContent className="space-y-3 p-4">
        <h2 className="text-lg font-semibold">Sign-Off</h2>
        <div className="grid grid-cols-2 gap-2 text-sm md:grid-cols-4">
          <Info label="Status" value={audit.status} />
          <Info label="Pending Verify" value={String(summary?.pending ?? 0)} />
          <Info label="Open Discrepancies" value={String(openDiscrepancies)} />
          <Info label="Signed Off" value={audit.signed_off_at ? fmtDateTime(audit.signed_off_at) : "—"} />
        </div>

        {openDiscrepancies > 0 && (
          <div className="flex items-start gap-2 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4" />
            <div>{openDiscrepancies} discrepancies still need reconciliation before sign-off.</div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={printReport}><Printer className="mr-1 h-4 w-4" /> Audit Report</Button>
          {audit.status === "In Progress" && (
            <Button onClick={onComplete} disabled={(summary?.pending ?? 0) > 0 || complete.isPending}>Mark Completed</Button>
          )}
          {canSign && audit.status === "Completed" && (
            <Button onClick={() => setConfirmOpen(true)} disabled={openDiscrepancies > 0 || signoff.isPending}>
              <ShieldCheck className="mr-1 h-4 w-4" /> Sign Off
            </Button>
          )}
        </div>
        {!canSign && audit.status === "Completed" && (
          <p className="text-xs text-muted-foreground">Only Super Admin or Biomed Head can sign off.</p>
        )}
      </CardContent></Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sign off Audit</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to sign off and lock this audit? This action cannot be undone and no further changes will be allowed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onSignoff} disabled={signoff.isPending}>
              {signoff.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm Sign Off
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}
